import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UploadProvider } from './contexts/UploadContext';
import { ToastProvider } from './contexts/ToastContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Recording from './pages/Recording';
import OperatorFeeds from './pages/OperatorFeeds';
import Uploading from './pages/Uploading';
import AdminUserManagement from './pages/AdminUserManagement';
import AdminAllFeeds from './pages/AdminAllFeeds';
import AdminFailedUploads from './pages/AdminFailedUploads';
import AdminUploading from './pages/AdminUploading';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected Operator Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recording"
        element={
          <ProtectedRoute>
            <Recording />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feeds"
        element={
          <ProtectedRoute>
            <OperatorFeeds />
          </ProtectedRoute>
        }
      />
      <Route
        path="/uploading"
        element={
          <ProtectedRoute>
            <Uploading />
          </ProtectedRoute>
        }
      />

      {/* Protected Admin Routes */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute adminOnly>
            <AdminUserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/feeds"
        element={
          <ProtectedRoute adminOnly>
            <AdminAllFeeds />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/failed"
        element={
          <ProtectedRoute adminOnly>
            <AdminFailedUploads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/uploading"
        element={
          <ProtectedRoute adminOnly>
            <AdminUploading />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <ToastProvider>
        <AuthProvider>
          <UploadProvider>
            <AppRoutes />
          </UploadProvider>
        </AuthProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;
